import React, { useState } from 'react';
import { Col, Container, Row, Table, Button as RbButton } from 'react-bootstrap';
import Button from '../../../../../Abstracts/Button/Button';
import { getRequest, postRequest } from '../../../../../Helper/ApiClient';
import Content from '../../../Abstracts/Content/Content';
import BoothOverlay from "../../BoothOverlay/BoothOverlay";
import ExhibitorBoothPopup from "../../Exhibition/ExhibitorBoothPopup/ExhibitorBoothPopup";

export const MEDIA_KIT_STATUS = {
  IDLE: 'idle',
  BUILDING: 'building',
  READY: 'ready',
  FAILED: 'failed',
} as const;

export type CompleteMediaKitStatus =
  typeof MEDIA_KIT_STATUS[keyof typeof MEDIA_KIT_STATUS];

export interface IVtsExhibitor {
  id: number;
  name: string;
  description: string;
  boothNumberB2b: number;
  panoNumberB2b: number;
  logo_image_url: string;
  video_public_link: string;
  website_url: string;
  facebook_url: string;
  instagram_url: string;
  youtube_url: string;
  twitter_url: string;
  complete_media_kit_url: string;
  complete_media_kit_status: CompleteMediaKitStatus;
  complete_media_kit_built_at: string | null;
  complete_media_kit_byte_size: number;
  organiser: boolean;
  has_new_product_attributes?: boolean;
  has_samples?: boolean;
  has_highlights?: boolean;
  has_media_files?: boolean;
  package?: 'basic' | 'premium';
  package_set?: boolean;
  brand_erp_id?: number | null;
  ci_color?: string | null;
  full_color?: boolean;
  logo_url?: string | null;
  key_visual_url?: string | null;
  products_count?: number;
  discount_percent?: number;
  order_start?: string | null;
  order_end?: string | null;
}

/** Which booth UI the admin preview opens while P-4889 is under review. */
type PreviewUi = 'overlay' | 'legacy';

function MediaKitCell({ exhibitor }: { exhibitor: IVtsExhibitor }) {
  const [status, setStatus] = useState(exhibitor.complete_media_kit_status);
  const [builtAt, setBuiltAt] = useState(exhibitor.complete_media_kit_built_at);
  const [byteSize, setByteSize] = useState(exhibitor.complete_media_kit_byte_size);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const buildMutation = postRequest<{
    status: CompleteMediaKitStatus;
    url: string;
    built_at: string | null;
    byte_size: number;
  }>(`/api/virtual-trade-show/exhibitors/${exhibitor.id}/build-media-kit?force=1`);

  const deleteMutation = postRequest<{
    status: CompleteMediaKitStatus;
    url: string;
    built_at: string | null;
    byte_size: number;
  }>(`/api/virtual-trade-show/exhibitors/${exhibitor.id}/delete-media-kit`);

  const handleBuildClick = () => {
    setStatus(MEDIA_KIT_STATUS.BUILDING);
    setErrorMessage(null);
    buildMutation.mutate(new FormData(), {
      onSuccess: (data) => {
        setStatus(data.status);
        setBuiltAt(data.built_at);
        setByteSize(data.byte_size);
      },
      onError: (err) => {
        setStatus(MEDIA_KIT_STATUS.FAILED);
        setErrorMessage(err.message);
      },
    });
  };

  const handleDeleteClick = () => {
    if (!window.confirm(`Mediakit für "${exhibitor.name}" löschen?\n\nDer ZIP wird aus dem Bucket entfernt und der Stand auf "idle" zurückgesetzt — Customer-Download verschwindet, bis ein neuer Build läuft.`)) {
      return;
    }
    setErrorMessage(null);
    deleteMutation.mutate(new FormData(), {
      onSuccess: (data) => {
        setStatus(data.status);
        setBuiltAt(null);
        setByteSize(0);
      },
      onError: (err) => {
        setErrorMessage(err.message);
      },
    });
  };

  const formatBytes = (bytes: number) =>
    bytes > 0 ? `${(bytes / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB` : '—';

  const anyPending = buildMutation.isPending || deleteMutation.isPending;
  const canDelete = status !== MEDIA_KIT_STATUS.IDLE || byteSize > 0;

  return (
    <div>
      <Button onClick={handleBuildClick} disabled={anyPending}>
        {buildMutation.isPending ? 'Building…' : 'Rebuild media kit'}
      </Button>
      {canDelete && (
        <RbButton
          variant="outline-danger"
          onClick={handleDeleteClick}
          disabled={anyPending}
          style={{ marginLeft: 8 }}
        >
          {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        </RbButton>
      )}
      <div style={{ fontSize: '0.85em', marginTop: 4 }}>
        Status: <strong>{status}</strong>
        {builtAt && <> · Built: {new Date(builtAt).toLocaleString('de-DE')}</>}
        {' · '}{formatBytes(byteSize)}
      </div>
      {errorMessage && <div style={{ color: 'red', fontSize: '0.85em' }}>{errorMessage}</div>}
    </div>
  );
}

export default function Exhibitors() {
  const exhibitorsRequest = getRequest<IVtsExhibitor[] | undefined>(
    '/api/virtual-trade-show/exhibitors'
  );
  const [showModal, setShowModal] = useState(false);
  const [exhibitor, setExhibitor] = useState<IVtsExhibitor>();
  const [previewUi, setPreviewUi] = useState<PreviewUi>('overlay');
  const openPopup = (_exhibitor: IVtsExhibitor, ui: PreviewUi) => {
    setPreviewUi(ui);
    setShowModal(true);
    setExhibitor(_exhibitor);
  };

  return (
    <>
      <Content>
        <Container>
          <Row>
            <Col>
              <h1>Exhibitors</h1>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table>
                <thead>
                  <tr>
                    <th>Exhibitor Name</th>
                    <th>B2B</th>
                    <th>Pano No.</th>
                    <th>Booth No.</th>
                    <th>Media kit</th>
                  </tr>
                </thead>
                <tbody>
                  {exhibitorsRequest.isSuccess &&
                    exhibitorsRequest.data &&
                    exhibitorsRequest.data.map((exhibitor) => {
                      return (
                        <tr key={exhibitor.id}>
                          <td>{exhibitor.name}</td>
                          <td>
                            <Button
                              onClick={() => openPopup(exhibitor, 'overlay')}
                            >
                              B2B Popup
                            </Button>{' '}
                            <Button
                              color="gray"
                              onClick={() => openPopup(exhibitor, 'legacy')}
                            >
                              Old popup
                            </Button>
                          </td>
                          <td>{exhibitor.panoNumberB2b}</td>
                          <td>{exhibitor.boothNumberB2b}</td>
                          <td>
                            <MediaKitCell exhibitor={exhibitor} />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      </Content>
      {exhibitor && showModal && previewUi === 'legacy' && (
        <ExhibitorBoothPopup
          boothNumber={exhibitor.boothNumberB2b}
          onClose={() => {
            setShowModal(false);
          }}
        />
      )}
      {exhibitor && showModal && previewUi === 'overlay' && (
        <BoothOverlay
          boothNumber={exhibitor.boothNumberB2b}
          onClose={() => {
            setShowModal(false);
          }}
        />
      )}
    </>
  );
}
